import csrfHeaders from 'src/utils/csrfHeaders'
import { genericError } from './useRecord'

export const subscriptionIntervals = {
  monthly:   { interval: 'month', interval_count: 1 },
  quarterly: { interval: 'month', interval_count: 3 },
  annual:    { interval: 'year',  interval_count: 1 },
}

// Card errors (declines, bad CVC, expired, etc.) render against the payment
// inputs (`stripe` field); everything else is a form-level (`base`) error.
const isCardError = error => error.type == 'card_error' || [
  'expired_card',
  'incorrect_cvc',
  'incorrect_number',
  'invalid_cvc',
  'invalid_expiry_month',
  'invalid_expiry_year',
  'invalid_number',
  'card_declined',
].includes(error.code)

const useStripeServices = ({ i18n, stripeIntentsPath }) => {
  // Client-side Stripe.js errors (confirmCardPayment/confirmCardSetup):
  // error.message is already localized via the Elements locale, so surface
  // it directly.
  const normalizeStripeError = error =>
    ({ [isCardError(error) ? 'stripe' : 'base']: [ error.message ] })

  // Map the error shapes our stripe_intents endpoints render. Stripe doesn't
  // localize errors for us server-side, so:
  //   - our own messages are tagged `type: 'custom'` by the controller (e.g.
  //     UnsupportedAchAccountError's "select a different account") — surface
  //     them verbatim; they're intentional, actionable guidance.
  //   - a Stripe card decline keeps its specific message ("your card was
  //     declined", not a misleading "try again"); the common decline also
  //     localizes client-side.
  //   - everything else (Stripe technical errors — rate limit, connection,
  //     processing) → our localized generic, not an English technical string.
  const normalizeServerError = json =>
    json.type == 'custom' ? { base: [ json.message ] }
    : isCardError(json)   ? { stripe: [ json.message ] }
    :                       genericError(i18n)

  const createIntent = async (order_id, props) => {
    try {
      const response = await fetch(stripeIntentsPath, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ ...props, order_id, locale: i18n.locale }),
      })
      const json = response.status == 204 ? {} : await response.json()

      if (response.ok) {
        return json
      } else {
        // `code` passes through for callers that act on a specific error.
        return { errors: normalizeServerError(json), code: json.code }
      }
    } catch (error) {
      console.error('Error creating payment intent:', error)
      return { errors: genericError(i18n) }
    }
  }

  return { normalizeStripeError, normalizeServerError, createIntent }
}

export default useStripeServices
